// Payroll register for a run (payroll-service /register). Self-describing: the
// columns adapt to the run's earnings + statutory. Rendered by PayrollRegisterPage.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';

export type RegisterGroup = 'earning' | 'deduction' | 'net' | 'employer';

export interface RegisterColumn {
  key: string;
  label: string;
  group: RegisterGroup;
  subtotal: boolean;
}

export interface RegisterRow {
  no: number;
  name: string;
  values: Record<string, string>;
}

export interface PayrollRegister {
  company: string;
  period: string; // YYYY-MM
  currency: string;
  country: string;
  runState: string;
  logoUrl: string;
  employeeCount: number;
  employerCost: string;
  columns: RegisterColumn[];
  employees: RegisterRow[];
  totals: Record<string, string>;
}

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

// Downloads the monthly payroll package (ZIP: register + statutory + bank file +
// payslips index) for a run. Fetches with auth then triggers a browser download.
export async function downloadPayrollPackage(runId: string, filename: string): Promise<void> {
  const res = await http.get(`/api/v1/payroll-runs/${runId}/package`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function getPayrollRegister(runId: string): Promise<PayrollRegister> {
  const d = (await http.get<any>(`/api/v1/payroll-runs/${runId}/register`)).data;
  return {
    company: d.company ?? '',
    period: d.period ?? '',
    currency: d.currency ?? 'TZS',
    country: d.country ?? '',
    runState: d.run_state ?? '',
    logoUrl: d.logo_url ?? '',
    employeeCount: Number(d.employee_count ?? 0),
    employerCost: d.employer_cost ?? '0',
    columns: (d.columns ?? []) as RegisterColumn[],
    employees: (d.employees ?? []) as RegisterRow[],
    totals: (d.totals ?? {}) as Record<string, string>,
  };
}
